

import React, { memo, useCallback, useMemo, useState } from 'react'

const GraniteTextureRail = ({
    items = [],
    activeKey = '',
    disabled = false,
    contextLabel = 'Подписка / обычная цена',
    showContext = true,
    ariaLabel = 'Выбор вида гранита',
    className = '',
    collapsible = true,
    collapsed,
    defaultCollapsed = true,
    collapseLabel = 'Гранит',
    expandedHint = 'Меняется только полированная поверхность',
    onCollapsedChange,
    onChange,
}) => {
    const safeItems = useMemo(() => (
        (Array.isArray(items) ? items : []).filter((item) => item?.key)
    ), [items])
    const controlledCollapsed = typeof collapsed === 'boolean'
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed === true)
    const isCollapsed = collapsible && (controlledCollapsed ? collapsed : internalCollapsed)
    const activeItem = useMemo(() => (
        safeItems.find((item) => String(item.key) === String(activeKey || ''))
        || safeItems.find((item) => item?.isDefault === true)
        || safeItems[0]
        || null
    ), [activeKey, safeItems])
    const activeTextureUrl = String(activeItem?.textureUrl || '').trim()
    const activeLabel = String(activeItem?.label || activeItem?.name || activeItem?.key || 'Не выбран')

    const setCollapsed = useCallback((nextValue) => {
        const safeValue = nextValue === true

        if (!controlledCollapsed) {
            setInternalCollapsed(safeValue)
        }

        onCollapsedChange?.(safeValue)
    }, [controlledCollapsed, onCollapsedChange])

    const toggleCollapsed = useCallback(() => {
        if (disabled) return
        setCollapsed(!isCollapsed)
    }, [disabled, isCollapsed, setCollapsed])

    return (
        <aside
            className={[
                'graniteTextureRail',
                'graniteTextureRail--stackedCards',
                isCollapsed ? 'is-collapsed' : 'is-expanded',
                className,
            ].filter(Boolean).join(' ')}
            aria-label={ariaLabel}
        >
            {collapsible ? (
                <button
                    type="button"
                    className="graniteTextureRail__collapseToggle"
                    onClick={toggleCollapsed}
                    disabled={disabled}
                    aria-expanded={!isCollapsed}
                    title={isCollapsed ? 'Развернуть выбор гранита' : 'Свернуть выбор гранита'}
                >
                    <span
                        className={`graniteTextureRail__collapseSwatch ${activeTextureUrl ? 'has-texture' : ''}`}
                        style={activeTextureUrl ? { backgroundImage: `url(${activeTextureUrl})` } : undefined}
                        aria-hidden="true"
                    >
                        {!activeTextureUrl ? activeLabel.slice(0, 1).toUpperCase() : null}
                    </span>

                    <span className="graniteTextureRail__collapseText">
                        <strong>{collapseLabel}</strong>
                        <small>{isCollapsed ? activeLabel : expandedHint}</small>
                    </span>

                    <span className="graniteTextureRail__collapseChevron" aria-hidden="true">
                        {isCollapsed ? '▸' : '▾'}
                    </span>
                </button>
            ) : null}

            {!isCollapsed ? (
                <div className="graniteTextureRail__panel">
                    {showContext ? (
                        <div className="graniteTextureRail__context">
                            <span>Показываются цены</span>
                            <strong>{contextLabel}</strong>
                        </div>
                    ) : null}

                    <div className="graniteTextureRail__list">
                        {safeItems.map((item) => {
                            const key = String(item.key)
                            const label = String(item.label || item.name || key)
                            const textureUrl = String(item.textureUrl || '').trim()
                            const isActive = key === String(activeKey || '')
                            const configured = item.configured !== false
                            const itemDisabled = disabled || item.disabled === true

                            return (
                                <button
                                    type="button"
                                    key={key}
                                    className={[
                                        'graniteTextureRail__item',
                                        isActive ? 'is-active' : '',
                                        item.isDefault ? 'is-default' : '',
                                        item.muted ? 'is-muted' : '',
                                        configured ? 'is-configured' : 'is-unconfigured',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => onChange?.(key, item)}
                                    disabled={itemDisabled}
                                    aria-pressed={isActive}
                                    aria-label={`Выбрать ${label}`}
                                    title={[
                                        label,
                                        item.subtitleLabel,
                                        item.priceLabel,
                                        item.sizeLabel,
                                        item.hint,
                                    ].filter(Boolean).join(' · ')}
                                >
                                    <span
                                        className={`graniteTextureRail__texture ${textureUrl ? 'has-texture' : ''}`}
                                        style={textureUrl ? { backgroundImage: `url(${textureUrl})` } : undefined}
                                        aria-hidden="true"
                                    >
                                        {!textureUrl ? label.slice(0, 1).toUpperCase() : null}
                                    </span>

                                    <span className="graniteTextureRail__content">
                                        <strong>{label}</strong>
                                        {item.subtitleLabel ? (
                                            <small className="graniteTextureRail__priceValue graniteTextureRail__metaValue">
                                                {item.subtitleLabel}
                                            </small>
                                        ) : null}
                                        {item.priceLabel ? (
                                            <small className="graniteTextureRail__priceValue">
                                                {item.priceLabel}
                                            </small>
                                        ) : null}
                                        {item.sizeLabel ? (
                                            <small className="graniteTextureRail__sizeValue">
                                                {item.sizeLabel}
                                            </small>
                                        ) : null}
                                    </span>

                                    <span
                                        className="graniteTextureRail__status"
                                        aria-hidden="true"
                                        title={configured ? 'Цена настроена' : 'Цена не настроена'}
                                    />

                                    {item.isDefault ? (
                                        <span className="graniteTextureRail__default" aria-hidden="true">★</span>
                                    ) : null}

                                    {isActive ? (
                                        <span className="graniteTextureRail__active" aria-hidden="true">✓</span>
                                    ) : null}
                                </button>
                            )
                        })}
                    </div>
                </div>
            ) : null}
        </aside>
    )
}

export default memo(GraniteTextureRail)
